// Google Identity Services (GIS) — flujo de ID token. Reemplaza MSAL.
//
// El usuario firma con Google; GIS entrega un ID token (JWT) que guardamos y mandamos
// como Bearer al backend (que lo valida). Los ID tokens caducan ~1h: al expirar, el
// backend responde 401, limpiamos el token y se vuelve a pedir login.
declare global {
  interface Window {
    google?: any
  }
}

import { Capacitor } from '@capacitor/core'

const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string
/** En la app nativa el flujo es distinto: Google BLOQUEA su OAuth dentro de WebViews
 *  (disallowed_useragent), así que ahí se usa el selector de cuentas del sistema. */
export const isNativeApp = (): boolean => Capacitor.isNativePlatform()
const HD = import.meta.env.VITE_GOOGLE_HD as string | undefined // dominio Workspace (hint)
const TOKEN_KEY = 'hd_id_token'

export const getToken = (): string | null => localStorage.getItem(TOKEN_KEY)
export const setToken = (t: string): void => localStorage.setItem(TOKEN_KEY, t)
export const clearToken = (): void => localStorage.removeItem(TOKEN_KEY)

/** ¿Hay un ID token no expirado? (solo lee `exp`; la firma la valida el backend). */
export function tokenValid(): boolean {
  const t = getToken()
  if (!t) return false
  try {
    const { exp } = JSON.parse(atob(t.split('.')[1]))
    return typeof exp === 'number' && exp * 1000 > Date.now() + 10_000
  } catch {
    return false
  }
}

let gisLoaded: Promise<void> | null = null
function loadGis(): Promise<void> {
  if (gisLoaded) return gisLoaded
  gisLoaded = new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve()
    const s = document.createElement('script')
    s.src = 'https://accounts.google.com/gsi/client'
    s.async = true
    s.defer = true
    s.onload = () => resolve()
    s.onerror = () => reject(new Error('No se pudo cargar Google Sign-In'))
    document.head.appendChild(s)
  })
  return gisLoaded
}

let initDone: Promise<void> | null = null
/** Inicializa GIS una sola vez. Al recibir credencial: guarda el token y recarga en la raíz. */
function ensureInit(): Promise<void> {
  if (initDone) return initDone
  initDone = loadGis().then(() => {
    window.google.accounts.id.initialize({
      client_id: CLIENT_ID,
      callback: (resp: { credential: string }) => {
        setToken(resp.credential)
        window.location.assign('/') // recarga limpia: useLoadProfile toma el token
      },
      auto_select: true,
      hd: HD || undefined,
    })
  })
  return initDone
}

/** Prepara GIS (para One Tap y el botón). Llamar una vez en la raíz de la app. */
export async function initGoogleAuth(): Promise<void> {
  if (isNativeApp()) return // en nativo no hay GIS: el login va por el plugin
  await ensureInit()
  window.google.accounts.id.prompt() // One Tap (no molesta si ya hay sesión)
}

// ── Login NATIVO (app Android) ──
// El selector de cuentas del sistema devuelve un ID token de Google. Se manda al
// backend igual que el de la web: el backend NO distingue ni necesita cambios,
// porque valida el mismo `aud` (el Client ID **web**, que es el serverClientId).
let nativeReady = false

async function ensureNativeInit() {
  const { SocialLogin } = await import('@capgo/capacitor-social-login')
  if (!nativeReady) {
    await SocialLogin.initialize({ google: { webClientId: CLIENT_ID } })
    nativeReady = true
  }
  return SocialLogin
}

/** Inicia sesión con la cuenta del dispositivo. Retorna true si se obtuvo token. */
export async function loginNative(): Promise<boolean> {
  const SocialLogin = await ensureNativeInit()
  // SIN `scopes` A PROPOSITO: el plugin rechaza cualquier lista de scopes salvo que
  // se modifique la MainActivity de Android ("You CANNOT use scopes without modifying
  // the main activity"), y de todos modos ya pide openid+email+profile por omision,
  // que es justo lo que necesitamos para el ID token.
  // NI `style: 'bottom'`: esa variante (GetGoogleIdOption) se probó al diagnosticar y no
  // sirve aquí — en ese modo el plugin nunca llama a setFilterByAuthorizedAccounts(false),
  // así que queda el valor por omisión de Google (true) y SOLO aparecen las cuentas que ya
  // autorizaron este client ID. Un repartidor nuevo, que jamás entró por la web, no vería
  // su cuenta. El flujo estándar (GetSignInWithGoogleOption) las muestra todas.
  const res = await SocialLogin.login({ provider: 'google', options: {} })
  // El plugin ha cambiado la forma de la respuesta entre versiones: se buscan las
  // variantes conocidas en vez de asumir una sola.
  const r = (res as Record<string, unknown>)?.result ?? res
  const idToken =
    (r as Record<string, unknown>)?.idToken ??
    ((r as Record<string, unknown>)?.authentication as Record<string, unknown> | undefined)?.idToken
  if (typeof idToken === 'string' && idToken) {
    setToken(idToken)
    return true
  }
  return false
}

/** Dibuja el botón oficial "Iniciar sesión con Google" en el elemento dado. */
export async function renderGoogleButton(el: HTMLElement): Promise<void> {
  await ensureInit()
  window.google.accounts.id.renderButton(el, {
    theme: 'filled_blue',
    size: 'large',
    text: 'signin_with',
    shape: 'pill',
    locale: 'es',
    width: 280,
  })
}

/** One Tap manual (botón "Iniciar sesión" propio). */
export function promptGoogle(): void {
  ensureInit().then(() => window.google?.accounts?.id?.prompt())
}

export function googleSignOut(): void {
  clearToken()
  if (isNativeApp()) {
    import('@capgo/capacitor-social-login')
      .then(({ SocialLogin }) => SocialLogin.logout({ provider: 'google' }))
      .catch(() => { /* best-effort */ })
    return
  }
  window.google?.accounts?.id?.disableAutoSelect()
}
